import type { TFunction } from 'react-i18next'
import type { Dispatch } from '@reduxjs/toolkit'
import type { Business, User } from '@pbt/pbt-ui-components'

import type {
  FailedRefundTransaction,
  RefundInvoice,
} from '~/api/graphql/generated/types'
import { triggerErrorDoggo } from '~/utils/errorDoggoUtils'

import { TransactionFailureReasonName } from '../../../add-payment-dialog-v2/utils/paymentErrorMapping'
import {
  trackLinkedRefundInitiationError,
  trackLinkedRefundProcessing,
} from './refundDialogTrackingUtils'
import {
  extractRefundFailureReasonName,
  getGraphQLRefundErrorMessage,
  isTransactionNotSettledError,
  triggerErrorDoggoForRefundInitiationErrorIfNeeded,
} from './refundErrorMapping'

interface InitiateLinkedRefundTransactionDependencies {
  currentBusiness?: Business | null
  currentUser?: User | null
  dispatch: (action: any) => void
  initiateRefundTransactionMutation: (options: {
    variables: {
      input: {
        amount: number
        externalDeviceId: string | null
        originalPaymentId: string
        refundInvoiceId: string
      }
    }
  }) => Promise<any>
  reduxDispatch: Dispatch<any>
  refundAmount: number
  refundInvoice: RefundInvoice
  selectedPaymentId: string
  setIsLoading: (isLoading: boolean) => void
  t: TFunction<'Dialogs'>
}

/**
 * Handles the linked refund transaction mutation with error handling.
 * Unlike unlinked refunds, this does not pass an externalDeviceId and does not
 * update the RefundFlowState or set isLoading to false on success.
 * On success, it sets the transaction ID and enables subscription listening.
 */
export const initiateLinkedRefundTransaction = async (
  deps: InitiateLinkedRefundTransactionDependencies,
): Promise<void> => {
  const {
    dispatch,
    reduxDispatch,
    initiateRefundTransactionMutation,
    refundAmount,
    selectedPaymentId,
    refundInvoice,
    setIsLoading,
    t,
    currentUser,
    currentBusiness,
  } = deps

  try {
    const result = await initiateRefundTransactionMutation({
      variables: {
        input: {
          amount: refundAmount,
          originalPaymentId: selectedPaymentId,
          refundInvoiceId: refundInvoice.id,
          // Linked refunds do not require an external device ID
          externalDeviceId: null,
        },
      },
    })

    if (
      !result?.data?.initiateRefundTransaction ||
      result.data?.initiateRefundTransaction?.error?.failureReason ||
      result.errors
    ) {
      dispatch({ type: 'CANCEL_TRANSACTION' })
      // Check for specific GraphQL error types to show appropriate message
      const graphQLErrorMessage = getGraphQLRefundErrorMessage(
        result.errors,
        true, // linked = true
      )
      const errorMessage =
        graphQLErrorMessage ||
        t('Dialogs:REFUND_DIALOG_V2.ERRORS.REFUND_INITIATION_FAILURE_LINKED')
      const backendErrorMessage =
        result.data?.initiateRefundTransaction?.error?.message ||
        result.errors?.[0]?.message ||
        errorMessage

      // Extract failure reason for tracking and fallback logic
      const failureReason = result.data?.initiateRefundTransaction?.error
        ?.failureReason
        ? extractRefundFailureReasonName(
            result.data?.initiateRefundTransaction as FailedRefundTransaction,
          )
        : 'GRAPHQL_ERROR'

      // Track linked refund initiation error (GraphQL error or transaction error)
      trackLinkedRefundInitiationError({
        refundInvoice,
        selectedPaymentId,
        refundAmount,
        businessId: currentBusiness?.id || '',
        transactionId: result.data?.initiateRefundTransaction?.id || null,
        failureReason: failureReason || 'GRAPHQL_ERROR',
        errorMessage: backendErrorMessage,
      })
      triggerErrorDoggoForRefundInitiationErrorIfNeeded({
        errors: result.errors,
        dispatch: reduxDispatch,
        backendErrorMessage,
        currentUser,
        currentBusiness,
      })
      dispatch({
        type: 'SET_REFUND_ERROR',
        payload: {
          message: errorMessage,
          severity: 'error',
          status: 500,
        },
      })
      // If failure reason is UNKNOWN or TRANSACTION_NOT_SETTLED, do not fall back to unlinked flow
      const failureReasonForFallback =
        failureReason !== 'GRAPHQL_ERROR' ? failureReason : null
      const shouldNotFallbackToUnlinked =
        failureReasonForFallback === TransactionFailureReasonName.UNKNOWN ||
        failureReasonForFallback ===
          TransactionFailureReasonName.TRANSACTION_NOT_SETTLED ||
        isTransactionNotSettledError(result.errors)

      if (failureReasonForFallback && !shouldNotFallbackToUnlinked) {
        dispatch({ type: 'SET_LINKED', payload: false })
        dispatch({ type: 'SET_UNLINKED', payload: true })
        dispatch({ type: 'SET_LINKED_REFUND_LISTENING', payload: false })
      }
      setIsLoading(false)
    } else {
      const transactionId = result.data.initiateRefundTransaction.id
      dispatch({
        type: 'SET_TRANSACTION_ID',
        payload: transactionId,
      })
      if (result.data.initiateRefundTransaction.externalDeviceId) {
        dispatch({
          type: 'SET_EXTERNAL_DEVICE_ID',
          payload: result.data.initiateRefundTransaction.externalDeviceId,
        })
      }
      dispatch({
        type: 'SET_LINKED_REFUND_LISTENING',
        payload: true,
      })
      trackLinkedRefundProcessing({
        refundInvoice,
        selectedPaymentId,
        refundAmount,
        businessId: currentBusiness?.id || '',
        transactionId,
      })
    }
  } catch (err) {
    dispatch({ type: 'CANCEL_TRANSACTION' })
    const errorMessage = t(
      'Dialogs:REFUND_DIALOG_V2.ERRORS.REFUND_INITIATION_FAILURE_LINKED',
    )
    const backendErrorMessage = (err as any)?.message || errorMessage

    // Track linked refund initiation error (network/exception)
    trackLinkedRefundInitiationError({
      refundInvoice,
      selectedPaymentId,
      refundAmount,
      businessId: currentBusiness?.id || '',
      failureReason: 'INITIATION_ERROR',
      errorMessage: backendErrorMessage,
    })

    triggerErrorDoggo(
      reduxDispatch,
      backendErrorMessage,
      500,
      currentUser,
      currentBusiness,
    )
    dispatch({
      type: 'SET_REFUND_ERROR',
      payload: {
        message: errorMessage,
        severity: 'error',
        status: 500,
      },
    })
    dispatch({ type: 'SET_LINKED', payload: false })
    dispatch({ type: 'SET_UNLINKED', payload: true })
    dispatch({ type: 'SET_LINKED_REFUND_LISTENING', payload: false })
    setIsLoading(false)
  }
}
